import * as Yup from "yup";
import { useState } from "react";
import { Form, Formik } from "formik";
import { toast } from "react-toastify";
import { useRecoilValue } from "recoil";
import { IconTrash } from "@tabler/icons";
import { useLocation } from "react-router-dom";
import useStyles from "../../../../styles/styles";
import keyLabel from "../../../../utils/keylabel";
import { configState } from "../../../../recoil/atoms";
import MainCard from "../../../../styles/ui-component/cards/MainCard";
import { InputField, SelectField } from "../../../../utils/FormFields";
import { Button, Grid, Divider, Typography, Tooltip } from "@mui/material";
import { CreateNewTemplateRequestFields, InventoryTemplateApi, CreateNewTemplateRequestFieldsDataTypeEnum } from "../../../../api/medical-operations";

const dataType = keyLabel(CreateNewTemplateRequestFieldsDataTypeEnum);

interface Field {
  id: number;
}

export default function AddField() {
  const classes = useStyles();
  const config = useRecoilValue(configState);
  const location = useLocation();
  const collectionID = location.state as string;

  const [fields, setFields] = useState<Field[]>([{ id: 1 }]);
  const [formValues, setFormValues] = useState({
    FieldName_1: "",
    DataType_1: CreateNewTemplateRequestFieldsDataTypeEnum.Text,
  });

  const handleAddField = () => {
    const newFieldId = fields.length + 1;
    setFields([...fields, { id: newFieldId }]);
  };

  const handleRemoveField = (id: number) => {
    if (id !== 1) {
      const updatedFields = fields.filter((field) => field.id !== id);
      setFields(updatedFields);
    }
  };

  const addNewField = (values: CreateNewTemplateRequestFields[]) => {
    const field = new InventoryTemplateApi(config);
    field
      .addNewField(collectionID, values)
      .then((res) => {
        toast.success(res?.data?.Message!);
      })
      .catch((err) => {
        toast.error(err?.response?.data?.detail);
      });
  };

  return (
    <Formik
      initialValues={formValues}
      enableReinitialize={true}
      validationSchema={Yup.object().shape({        
        ...fields.reduce((acc, field) => {
          acc[`FieldName_${field.id}`] = Yup.string().required("Required");
          acc[`DataType_${field.id}`] = Yup.string().required("Required");
          return acc;
        }, {}),
      })}
      onSubmit={(values) => {
        const fieldArray: CreateNewTemplateRequestFields[] = fields.map((field) => ({
          FieldName: values[`FieldName_${field.id}`],
          DataType: values[`DataType_${field.id}`],
        }));
        addNewField(fieldArray);
      }}
    >
      {({ isSubmitting }: any) => (
        <Form>
          <MainCard>
            <Grid container spacing={2} sx={{ pr: 2 }}>
              <Typography variant="h3" sx={{ ml: 2.5, mt: 2 }}>
                Add New Fields
              </Typography>
              <Divider
                variant="middle"
                sx={{ mt: 2, width: "100%", borderColor: "rgba(0, 0, 0, 0.30)" }}
              />
              {fields.map((field) => (
                <Grid container item xs={12} spacing={2} key={field.id}>
                  <Grid item xs={6}>
                    <InputField
                      name={`FieldName_${field.id}`}
                      label="Field Name"
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <SelectField
                      name={`DataType_${field.id}`}
                      label="DataType"
                      data={dataType}
                      type={CreateNewTemplateRequestFieldsDataTypeEnum}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={1}>
                    {field.id !== 1 && (
                      <Tooltip title="Remove Field">
                        <Button
                          variant="outlined"
                          color="error"
                          sx={{ mt: 1 }}
                          onClick={() => handleRemoveField(field.id)}
                        >
                          <IconTrash />
                        </Button>
                      </Tooltip>
                    )}
                  </Grid>
                </Grid>
              ))}
              <Grid item xs={12}>
                <Button variant="outlined" color="primary" onClick={handleAddField}>
                  Add Field
                </Button>
              </Grid>
              <Grid item xs={12} sx={{ display: "flex", justifyContent: "flex-end", mt: 4 }}>
                <Button
                  type="submit"
                  variant="contained"
                  color="secondary"
                  className={classes.button}                  
                >
                  Submit
                </Button>
              </Grid>
            </Grid>
          </MainCard>
        </Form>
      )}
    </Formik>
  );
}
